/**
 * Mock trader state generators for UI development.
 *
 * Produces realistic orders, positions, and game data so the Position Tracker
 * and Value Dashboard can be inspected without live API connections.
 *
 * Activate via the Test Data toggle in the Position Tracker.
 */

import type { KalshiOrder } from '../lib/kalshiApi';
import type { Position } from '../types';
import type { ConsolidatedGameBooks } from '../lib/nbaConsolidated/types';
import type { GameData, SpreadGameData, SpreadMarketInfo } from '../lib/sportsStream/types';

interface MockGame {
  date: string;
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  startTimePt: string;
  eventTicker: string;
}

const NBA_GAMES: MockGame[] = [
  {
    date: '2026-02-24',
    awayCode: 'LAL',
    homeCode: 'BOS',
    awayName: 'Los Angeles Lakers',
    homeName: 'Boston Celtics',
    startTimePt: '4:30 PM',
    eventTicker: 'KXNBA-26FEB24-LAL-BOS',
  },
  {
    date: '2026-02-24',
    awayCode: 'GSW',
    homeCode: 'MIA',
    awayName: 'Golden State Warriors',
    homeName: 'Miami Heat',
    startTimePt: '5:00 PM',
    eventTicker: 'KXNBA-26FEB24-GSW-MIA',
  },
  {
    date: '2026-02-24',
    awayCode: 'NYK',
    homeCode: 'PHI',
    awayName: 'New York Knicks',
    homeName: 'Philadelphia 76ers',
    startTimePt: '4:00 PM',
    eventTicker: 'KXNBA-26FEB24-NYK-PHI',
  },
  {
    date: '2026-02-25',
    awayCode: 'DEN',
    homeCode: 'DAL',
    awayName: 'Denver Nuggets',
    homeName: 'Dallas Mavericks',
    startTimePt: '5:30 PM',
    eventTicker: 'KXNBA-26FEB25-DEN-DAL',
  },
  {
    date: '2026-02-25',
    awayCode: 'MIL',
    homeCode: 'CHI',
    awayName: 'Milwaukee Bucks',
    homeName: 'Chicago Bulls',
    startTimePt: '5:00 PM',
    eventTicker: 'KXNBA-26FEB25-MIL-CHI',
  },
];

function makeTicker(game: MockGame, side: 'away' | 'home'): string {
  const code = side === 'away' ? game.awayCode : game.homeCode;
  const dateStr = game.date.replace(/-/g, '').slice(2);
  return `${game.eventTicker}-${code}-${dateStr}`;
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomChoice<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

let orderCounter = 0;

function makeOrderId(): string {
  orderCounter += 1;
  const hex = orderCounter.toString(16).padStart(8, '0');
  return `mock-${hex}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateMockOrders(games: MockGame[] = NBA_GAMES): KalshiOrder[] {
  const orders: KalshiOrder[] = [];

  for (const game of games) {
    const orderCount = randomInt(1, 5);
    for (let i = 0; i < orderCount; i++) {
      const side = randomChoice(['yes', 'no'] as const);
      const price = randomInt(15, 85);
      orders.push({
        order_id: makeOrderId(),
        ticker: makeTicker(game, randomChoice(['away', 'home'])),
        side,
        action: 'buy',
        type: 'limit',
        yes_price: side === 'yes' ? price : undefined,
        no_price: side === 'no' ? price : undefined,
        remaining_count: randomInt(1, 50),
        status: randomChoice(['resting', 'resting', 'resting', 'pending']),
        queue_position: randomInt(1, 100),
        created_time: new Date(Date.now() - randomInt(60_000, 3_600_000)).toISOString(),
      });
    }
  }

  return orders;
}

export function generateMockPositions(games: MockGame[] = NBA_GAMES): Position[] {
  const positions: Position[] = [];

  for (const game of games.slice(0, 4)) {
    const posCount = randomInt(1, 3);
    for (let i = 0; i < posCount; i++) {
      const side = randomChoice(['yes', 'no'] as const);
      const avgPrice = randomInt(20, 80);
      const currentPrice = avgPrice + randomInt(-15, 15);
      const clampedCurrent = Math.max(1, Math.min(99, currentPrice));
      const quantity = randomInt(5, 100);
      const unrealizedPnl = (clampedCurrent - avgPrice) * quantity * (side === 'yes' ? 1 : -1);
      const realizedPnl = randomInt(-500, 500);
      const totalPnl = realizedPnl + unrealizedPnl;

      positions.push({
        ticker: makeTicker(game, randomChoice(['away', 'home'])),
        side,
        quantity,
        averagePrice: avgPrice,
        currentPrice: clampedCurrent,
        realizedPnl,
        unrealizedPnl,
        totalPnl,
        pnlPercent: avgPrice > 0 ? (totalPnl / (avgPrice * quantity)) * 100 : 0,
      });
    }
  }

  return positions;
}

function makeBookLevels(basePrice: number, depth: number = 3) {
  const levels = [];
  for (let i = 0; i < depth; i++) {
    const price = Math.max(1, Math.min(99, basePrice - i * randomInt(1, 3)));
    levels.push({ priceCents: price, size: randomInt(10, 500) });
  }
  return levels;
}

export function generateMockGameData(games: MockGame[] = NBA_GAMES): {
  consolidatedGames: ConsolidatedGameBooks[];
  sportsGames: Map<string, GameData[]>;
  positionTitleByTicker: Record<string, string>;
  positionGameDateByTicker: Record<string, string>;
  positionOutcomeByTicker: Record<string, { yes?: string; no?: string }>;
} {
  const consolidated: ConsolidatedGameBooks[] = [];
  const spreadGames: GameData[] = [];
  const positionTitleByTicker: Record<string, string> = {};
  const positionGameDateByTicker: Record<string, string> = {};
  const positionOutcomeByTicker: Record<string, { yes?: string; no?: string }> = {};

  for (const game of games) {
    const key = `${game.date}|${game.awayCode}|${game.homeCode}`;
    const awayPrice = randomInt(30, 70);
    const homePrice = 100 - awayPrice;

    const awayTicker = makeTicker(game, 'away');
    const homeTicker = makeTicker(game, 'home');

    consolidated.push({
      key,
      date: game.date,
      awayCode: game.awayCode,
      homeCode: game.homeCode,
      awayName: game.awayName,
      homeName: game.homeName,
      startTimePt: game.startTimePt,
      kalshi: {
        venue: 'kalshi',
        eventId: game.eventTicker,
        awayCode: game.awayCode,
        homeCode: game.homeCode,
        markets: {
          away: {
            marketTicker: awayTicker,
            yes: makeBookLevels(awayPrice),
            no: makeBookLevels(100 - awayPrice),
          },
          home: {
            marketTicker: homeTicker,
            yes: makeBookLevels(homePrice),
            no: makeBookLevels(100 - homePrice),
          },
        },
        tsMs: Date.now(),
      },
      polymarket: {
        venue: 'polymarket',
        eventId: `poly-${game.awayCode}-${game.homeCode}`,
        awayCode: game.awayCode,
        homeCode: game.homeCode,
        markets: {
          away: {
            marketTicker: `POLY-${game.awayCode}-ML`,
            yes: makeBookLevels(awayPrice + randomInt(-3, 3)),
            no: makeBookLevels(homePrice + randomInt(-3, 3)),
          },
          home: {
            marketTicker: `POLY-${game.homeCode}-ML`,
            yes: makeBookLevels(homePrice + randomInt(-3, 3)),
            no: makeBookLevels(awayPrice + randomInt(-3, 3)),
          },
        },
        tsMs: Date.now(),
      },
    });

    // Spread data
    const spreadMarkets = new Map<string, SpreadMarketInfo>();
    for (let s = -10; s <= 10; s += 2.5) {
      const code = s < 0 ? game.awayCode : game.homeCode;
      const mapKey = `${code}-${Math.abs(s)}`;
      const spreadPrice = 50 + randomInt(-20, 20);
      spreadMarkets.set(mapKey, {
        book: {
          marketTicker: `${game.eventTicker}-SPD-${mapKey}`,
          yes: makeBookLevels(spreadPrice),
          no: makeBookLevels(100 - spreadPrice),
        },
        floorStrike: s,
        teamCode: code,
      });
    }

    spreadGames.push({
      marketType: 'spread',
      eventTicker: game.eventTicker,
      date: game.date,
      awayCode: game.awayCode,
      homeCode: game.homeCode,
      awayName: game.awayName,
      homeName: game.homeName,
      startTimePt: game.startTimePt,
      markets: spreadMarkets,
    } as SpreadGameData);

    positionTitleByTicker[awayTicker] = `${game.awayName} ML`;
    positionTitleByTicker[homeTicker] = `${game.homeName} ML`;
    positionGameDateByTicker[awayTicker] = game.date;
    positionGameDateByTicker[homeTicker] = game.date;
    positionOutcomeByTicker[awayTicker] = {
      yes: `${game.awayCode} wins`,
      no: `${game.homeCode} wins`,
    };
    positionOutcomeByTicker[homeTicker] = {
      yes: `${game.homeCode} wins`,
      no: `${game.awayCode} wins`,
    };
  }

  const sportsGames = new Map<string, GameData[]>();
  sportsGames.set('nba-spread', spreadGames);

  return {
    consolidatedGames: consolidated,
    sportsGames,
    positionTitleByTicker,
    positionGameDateByTicker,
    positionOutcomeByTicker,
  };
}

export function generateMockTraderState() {
  const gameData = generateMockGameData();
  const orders = generateMockOrders();
  const positions = generateMockPositions();
  const totalPnl = positions.reduce((sum, p) => sum + p.totalPnl, 0);

  return {
    orders,
    positions,
    totalPnl,
    ...gameData,
  };
}
